import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { ApiService } from 'src/app/core/api/api.service';
import { MedicalRecordPayload, MedicalRecordResponse } from 'src/app/interfaces/gestion-clinica/medical-record.interface';

@Injectable({
  providedIn: 'root'
})
export class MedicalRecordsService {

  private readonly base = '/clinical/medical-records';

  constructor(private api: ApiService) {}

  // El token, perfil y sucursal salen de la sesión (Bearer); ya no se envían desde el cliente.

  pendingOrders(params: any): Observable<MedicalRecordResponse> {
    return this.api.post(`${this.base}/pending-orders/list`, {
      limit: params?.limit,
      offset: params?.offset,
      year: params?.year,
      search: this.buildSearch(params)
    });
  }

  orderContext(orderId: number | string): Observable<MedicalRecordResponse> {
    return this.api.post(`${this.base}/order-context`, { orderId });
  }

  createRecord(payload: MedicalRecordPayload): Observable<MedicalRecordResponse> {
    return this.api.post(`${this.base}/records/create`, this.withAudit(payload));
  }

  updateRecord(id: number | string, payload: Partial<MedicalRecordPayload>): Observable<MedicalRecordResponse> {
    return this.api.post(`${this.base}/records/update`, { id, ...this.withAudit(payload) });
  }

  records(params: any): Observable<MedicalRecordResponse> {
    return this.api.post(`${this.base}/records/list`, {
      limit: params?.limit,
      offset: params?.offset,
      search: this.buildSearch(params)
    });
  }

  record(id: number | string): Observable<MedicalRecordResponse> {
    return this.api.post(`${this.base}/records/find`, { id });
  }

  addEvolution(recordId: number | string, payload: any): Observable<MedicalRecordResponse> {
    return this.api.post(`${this.base}/records/evolutions/create`, { id: recordId, ...this.withAudit(payload) });
  }

  evolutions(recordId: number | string): Observable<MedicalRecordResponse> {
    return this.api.post(`${this.base}/records/evolutions/list`, { id: recordId });
  }

  relatedOrders(patientId: number | string): Observable<MedicalRecordResponse> {
    return this.api.post(`${this.base}/patients/orders/list`, { patientId });
  }

  audits(recordId: number | string): Observable<MedicalRecordResponse> {
    return this.api.post(`${this.base}/records/audits/list`, { id: recordId });
  }

  consultarIdentificacion(documento: string): Observable<any> {
    return this.api.post(`${this.base}/identification`, { documento: documento || '' });
  }

  // El backend v1 espera `search` como objeto { term, paciente, ... }. El cliente legacy
  // envía o un string simple (→ { term }) o los campos de búsqueda planos en el params
  // (term, fechaInicio, fechaFin, etc.). `perfil`/`sucursal` de sesión NO van en search.
  private buildSearch(params: any): any {
    const raw = params?.search;
    if (typeof raw === 'string') {
      return raw.trim() ? { term: raw.trim() } : {};
    }
    if (raw && typeof raw === 'object') {
      return raw;
    }

    const allowed = ['term', 'paciente', 'documento', 'orden', 'factura', 'medico', 'responsable', 'fecha', 'fechaInicio', 'fechaFin'];
    const search: any = {};
    for (const key of allowed) {
      const value = params?.[key];
      if (value !== undefined && value !== null && value !== '') {
        search[key] = value;
      }
    }
    return search;
  }

  private withAudit<T extends object>(payload: T): T & { audit: any } {
    return {
      ...payload,
      audit: {
        device: this.obtenerDispositivo(),
        platform: navigator.platform || '',
        language: navigator.language || '',
        screenResolution: `${window.screen.width}x${window.screen.height}`
      }
    };
  }

  private obtenerDispositivo(): string {
    const userAgent = navigator.userAgent || '';
    if (/tablet|ipad/i.test(userAgent)) return 'TABLET';
    if (/mobile|android|iphone/i.test(userAgent)) return 'MOVIL';
    return 'COMPUTADOR';
  }
}
